// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { popupErrorHandler } from '@shared/utils/AppHandlers';
import TerceraSLTPNumericTemplate from '@front/templates/TerceraSLTPNumericTemplate.html';
import { ContainerControl } from './ContainerControl';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { NumericUtils } from '@shared/utils/NumericUtils';
export class TerceraSLTPNumeric extends ContainerControl {
    constructor() {
        super();
        this.warnPopupTimeout = null;
        this.valueUpDownChangeProcess = (isUp, price) => {
            const step = this.get('step');
            const minValue = this.get('minValue');
            const maxValue = this.get('maxValue');
            return NumericUtils.valueUpDownChangeProcess(isUp, price, step, minValue, maxValue);
        };
    }
    getType() {
        return 'TerceraSLTPNumeric';
    }
    oninit() {
        super.oninit();
        this.observe('value', this.onValueChanged);
        this.observe('SLTPChecked', this.onSLTPChecked);
        this.observe('trStopChecked', this.onTrailingStopChecked);
        this.observe('SLTPChecked canEdit', this.onNumericAvailabilityChanged, { init: false });
        this.on('terceraNumericInputClick', this.onInputClick); // #86098
        void this.set('customErrorText', Resources.getResource('UserControl.TerceraSLTPNumeric.ValueIsEmpty'));
    }
    oncomplete() {
        super.oncomplete();
        this.observe('instrument', this.updateIncrementCoefficient);
        BaseSettings.insDefSettingsStorage.DefaultSettingsChanged.Subscribe(this.updateIncrementCoefficient, this);
    }
    onteardown() {
        BaseSettings.insDefSettingsStorage.DefaultSettingsChanged.UnSubscribe(this.updateIncrementCoefficient, this);
        super.onteardown();
    }
    GetInterval(value, forDecimalPlaces) {
        var _a;
        if (isNullOrUndefined((_a = this.Controls) === null || _a === void 0 ? void 0 : _a.terceraNumeric)) {
            return null;
        }
        return this.Controls.terceraNumeric.GetInterval(value, forDecimalPlaces);
    }
    onValueChanged(value) {
        this.fire(TerceraSLTPNumericEvents.ValueChanged, this, value);
    }
    onSLTPChecked(value) {
        this.fire(TerceraSLTPNumericEvents.SLTPChecked, this, value);
    }
    onTrailingStopChecked(value) {
        this.fire(TerceraSLTPNumericEvents.TrailingStopChecked, this, value);
    }
    setFocus(changeEnableState, needActivate) {
        super.setFocus();
        void this.set('numericFocused', true);
        this.Controls.terceraNumeric.setFocus(changeEnableState);
        if (needActivate) {
            this.onInputClick();
        }
    }
    gotFocus() {
        super.gotFocus();
        if (this.get('forChart')) {
            // #107212
            this.showPopupWarning();
        }
    }
    onInputClick() {
        if (!this.get('SLTPChecked')) {
            // #86098
            void this.set('SLTPChecked', true);
        }
    }
    showPopupWarning() {
        // #107212
        const cntrl = this.Controls.terceraNumeric;
        if (isNullOrUndefined(cntrl))
            return;
        if (!isNullOrUndefined(this.warnPopupTimeout)) {
            clearTimeout(this.warnPopupTimeout);
        }
        this.warnPopupTimeout = setTimeout(() => {
            const popup = popupErrorHandler;
            popup.Show(cntrl, Resources.getResource('UserControl.TerceraSLTPNumeric.ChartOffsetWarning'), '', false, null, '', false);
            this.warnPopupTimeout = setTimeout(() => {
                popup.Hide(cntrl);
            }, TerceraSLTPNumeric.WARNING_HIDE_TIME_MS);
        }, 10);
    }
    updateIncrementCoefficient() {
        var _a;
        const instrument = this.get('instrument');
        const insDefSettings = BaseSettings.insDefSettingsStorage.GetInstrumentSettings(instrument);
        this.set('insIncrementCoeff', (_a = insDefSettings === null || insDefSettings === void 0 ? void 0 : insDefSettings.PriceIncrementTicks) !== null && _a !== void 0 ? _a : 1);
    }
    onNumericAvailabilityChanged() {
        if (!this.isNumericEnabled()) {
            this.clearNumericFocus();
        }
    }
    isNumericEnabled() {
        return this.get('SLTPChecked') && this.get('canEdit');
    }
    clearNumericFocus() {
        var _a;
        const numeric = (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.terceraNumeric;
        const input = numeric.find('input');
        if (input === document.activeElement) {
            input.blur();
        }
        if (!isNullOrUndefined(numeric)) {
            popupErrorHandler.Hide(numeric);
        }
        void this.set('numericFocused', false);
    }
}
TerceraSLTPNumeric.WARNING_HIDE_TIME_MS = 3000;
export var TerceraSLTPNumericEvents;
(function (TerceraSLTPNumericEvents) {
    TerceraSLTPNumericEvents["ValueChanged"] = "ValueChanged";
    TerceraSLTPNumericEvents["SLTPChecked"] = "SLTPChecked";
    TerceraSLTPNumericEvents["TrailingStopChecked"] = "TrailingStopChecked";
})(TerceraSLTPNumericEvents || (TerceraSLTPNumericEvents = {}));
ContainerControl.extendWith(TerceraSLTPNumeric, {
    data: function () {
        return {
            instrument: null,
            insIncrementCoeff: 1,
            customErrorText: null,
            forChart: false, // #107212
            canEdit: true,
            rightAlignedText: '',
            rightAlignedTextAdditionalClass: '',
            isVisibleEnableButton: true,
            numericFocused: false
        };
    },
    template: TerceraSLTPNumericTemplate
});
